/**
 * The analysis dock (top right of the canvas, 2D and 3D): one toggle per layer with its key, the active layer's
 * legend and the value under the cursor, the month slider, Sun & shade (sun path diagram, date and time scrubber,
 * play), the cross-section's height profile, and in 3D the DSM cloud. Everything it changes goes through the store.
 *
 * The cursor readout is written straight into the DOM from TopView's hover callback (no React render per move);
 * the sun animation advances the store once per animation frame.
 */
import { Box, CircleDot, Gauge, Layers as LayersIcon, Mountain, Pause, Play, Ruler, Sun, SunMedium, X, Zap } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useRef, type ReactNode } from "react";
import type { IntlShape } from "react-intl";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import { zOnPlane, type Vec2 } from "../geo.ts";
import { formatCombo } from "../hotkeys.ts";
import { faceAt, type LiveRoof } from "../roof.ts";
import { ProfileChart, SunPath } from "./Charts";
import { gradientCss, niceTicks, normalize, type Domain, type ScaleName } from "./colors.ts";
import type { AnalysisData } from "./data.ts";
import { horizonProfile, profile, sampleBilinear, sampleNearest } from "./grid.ts";
import { activeRaster, kwhDomain, type ActiveRaster } from "./Overlay2D";
import type { PanelYield } from "./panels.ts";
import { useAnalysisState, type AnalysisStore, type Cloud, type RasterLayer } from "./store.ts";
import { dayDate, dayLength, solarClock, sunAtSolarTime } from "./sun.ts";

export interface DockApi {
  /** The local point under the pointer in the 2D view (null: off the view). */
  hover: (p: Vec2 | null) => void;
}

/** "21 Jun" in the reader's language. */
export function formatDayOf(intl: IntlShape, doy: number): string {
  return intl.formatDate(dayDate(doy), { day: "numeric", month: "short", timeZone: "UTC" });
}

/** A solar time ("14:30", "2:30 PM") in the reader's language. */
export function formatSolarTimeOf(intl: IntlShape, minutes: number): string {
  return intl.formatTime(solarClock(minutes), { hour: "numeric", minute: "2-digit", timeZone: "UTC" });
}

/** Simulated minutes of sun per real second while playing. */
const PLAY_SPEED = 90;

function Legend({ scale, domain, label, units, format }: { scale: ScaleName; domain: Domain; label: string; units: string; format: (v: number) => string }) {
  const ticks = niceTicks(domain, 5);
  return (
    <div className="an-legend">
      <div className="an-legend-title">
        <b>{label}</b> <span className="muted">{units}</span>
      </div>
      <div className="an-legend-bar" style={{ background: gradientCss(scale) }} />
      <div className="an-legend-ticks">
        {ticks.map((t) => (
          <span key={t} style={{ left: `${Math.min(100, Math.max(0, normalize(t, domain) * 100))}%` }}>
            {format(t)}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatter(intl: IntlShape, active: ActiveRaster): (v: number) => string {
  if (active.percent) return (v) => intl.formatNumber(Math.round(v * 100));
  return (v) => intl.formatNumber(v, Math.abs(v) >= 100 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: active.digits, maximumFractionDigits: active.digits });
}

function ToggleButton({ on, onClick, icon, label, combo, mac, disabled }: { on: boolean; onClick: () => void; icon: ReactNode; label: string; combo: string | null; mac: boolean; disabled?: boolean }) {
  const t = useT();
  const title = combo ? t("editor.dock.withKey", { label, combo: formatCombo(combo, mac) }) : label;
  return (
    <button type="button" className={`an-toggle${on ? " on" : ""}`} onClick={onClick} title={title} aria-label={title} aria-pressed={on} aria-keyshortcuts={combo ?? undefined} disabled={disabled}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

export function AnalysisDock(props: {
  store: AnalysisStore;
  data: AnalysisData | null;
  loading: boolean;
  error: string | null;
  view: "2d" | "3d";
  roof: LiveRoof;
  yields: PanelYield[] | null;
  mac: boolean;
  keyOf: (id: string) => string | null;
  onSectionTool: () => void;
  sectionTool: boolean;
  apiRef?: React.Ref<DockApi>;
}) {
  const { store, data, view, roof, yields, mac, keyOf } = props;
  const t = useT();
  const intl = useIntlShape();
  const tRef = useRef({ t, intl });
  tRef.current = { t, intl };
  const raster = useAnalysisState(store, (s) => s.raster);
  const month = useAnalysisState(store, (s) => s.month);
  const contours = useAnalysisState(store, (s) => s.contours);
  const panelKwh = useAnalysisState(store, (s) => s.panelKwh);
  const sun = useAnalysisState(store, (s) => s.sun);
  const day = useAnalysisState(store, (s) => s.day);
  const minutes = useAnalysisState(store, (s) => s.minutes);
  const playing = useAnalysisState(store, (s) => s.playing);
  const cloud = useAnalysisState(store, (s) => s.cloud);
  const section = useAnalysisState(store, (s) => s.section);
  const active = useMemo(() => activeRaster(data, { raster, month }), [data, raster, month]);
  const readout = useRef<HTMLDivElement>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const dataRef = useRef(data);
  dataRef.current = data;

  useImperativeHandle(props.apiRef, () => ({
    hover: (p) => {
      const el = readout.current;
      if (!el) return;
      const a = activeRef.current;
      const d = dataRef.current;
      const { t, intl } = tRef.current;
      if (!p || !a || !d) {
        el.textContent = a ? t("editor.dock.pointAtMap") : "";
        return;
      }
      const parts: string[] = [];
      const v = sampleNearest(a.raster, p);
      if (Number.isFinite(v)) {
        parts.push(
          a.percent
            ? t("editor.dock.readoutPercent", { label: t(a.label), value: intl.formatNumber(v, { style: "percent", maximumFractionDigits: 0 }) })
            : t("editor.dock.readoutValue", { label: t(a.label), value: formatter(intl, a)(v), units: a.units }),
        );
      }
      if (a.scale !== "height" && d.heightAboveGround) {
        const h = sampleBilinear(d.heightAboveGround, p);
        if (Number.isFinite(h)) parts.push(t("editor.dock.aboveGround", { height: intl.formatNumber(h, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }));
      }
      el.textContent = parts.length ? parts.join(" · ") : t("editor.dock.noData");
    },
  }));

  // the sun animation: the store advances once per frame; nights are skipped
  useEffect(() => {
    if (!playing || !data) return;
    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      const dt = last ? Math.min(0.1, (t - last) / 1000) : 0;
      last = t;
      const s = store.get();
      const span = dayLength(data.sun.lat, s.day);
      let m = s.minutes + dt * PLAY_SPEED;
      let d = s.day;
      if (span && (m > span.set || m < span.rise)) {
        if (m > span.set) d = (d % 365) + 1;
        m = dayLength(data.sun.lat, d)?.rise ?? 360;
      } else if (m >= 1440) {
        m -= 1440;
        d = (d % 365) + 1;
      }
      store.set({ minutes: m, day: d });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, data, store]);

  // the horizon over the roof's middle (the panels', else the faces'), for the sun path's shade mask
  const horizon = useMemo(() => {
    if (!data?.surface || !sun) return null;
    const pts = yields?.length ? yields.flatMap((y) => y.plan) : roof.faces.flatMap((f) => f.plan);
    if (!pts.length) return null;
    const c: Vec2 = [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
    const face = faceAt(roof, c);
    const z = face ? zOnPlane(face.normal, face.d, c[0], c[1]) : sampleNearest(data.surface, c);
    return Number.isFinite(z) ? horizonProfile(data.surface, c, z + 0.1) : null;
  }, [data, sun, yields, roof]);

  const profileData = useMemo(() => {
    if (!section || !data?.heightAboveGround || !data.surface) return null;
    const samples = profile([data.heightAboveGround, data.surface], section.a, section.b);
    const model = samples.map((s) => {
      const face = faceAt(roof, [s.x, s.y]);
      const ground = s.values[1]! - s.values[0]!;
      return face && Number.isFinite(ground) ? zOnPlane(face.normal, face.d, s.x, s.y) - ground : NaN;
    });
    return {
      distances: samples.map((s) => s.d),
      series: [
        { label: t("editor.dock.series.dsm"), className: "dsm", values: samples.map((s) => s.values[0]!) },
        { label: t("editor.dock.series.model"), className: "model", values: model },
      ],
    };
  }, [section, data, roof, t]);

  const set = store.set;
  const toggleRaster = (layer: RasterLayer) => set((s) => ({ raster: s.raster === layer ? "none" : layer }));
  const kwh = useMemo(() => (yields && panelKwh ? { domain: kwhDomain(yields), total: yields.reduce((s, y) => s + (Number.isFinite(y.kwh) ? y.kwh : 0), 0), estimated: yields.filter((y) => y.source === "estimate").length } : null), [yields, panelKwh]);
  const sunNow = sun && data ? sunAtSolarTime(data.sun.lat, day, minutes) : null;
  const span = sun && data ? dayLength(data.sun.lat, day) : null;
  const nothing = raster === "none" && !panelKwh && !sun && !section && cloud === "off";

  return (
    <div className="an-dock" role="region" aria-label={t("editor.dock.region")}>
      <div className="an-toggles" role="toolbar" aria-label={t("editor.dock.region")}>
        <LayersIcon size={14} className="an-toggles-icon" aria-hidden />
        <ToggleButton on={raster === "height"} onClick={() => toggleRaster("height")} icon={<Mountain size={14} />} label={t("editor.layers.height")} combo={keyOf("layer.height")} mac={mac} />
        <ToggleButton on={raster === "irradiance"} onClick={() => toggleRaster("irradiance")} icon={<SunMedium size={14} />} label={t("editor.layers.irradiance")} combo={keyOf("layer.irradiance")} mac={mac} />
        <ToggleButton on={raster === "access"} onClick={() => toggleRaster("access")} icon={<CircleDot size={14} />} label={t("editor.layers.access")} combo={keyOf("layer.access")} mac={mac} />
        <ToggleButton on={raster === "tsrf"} onClick={() => toggleRaster("tsrf")} icon={<Gauge size={14} />} label={t("editor.layers.tsrf")} combo={keyOf("layer.tsrf")} mac={mac} />
        <ToggleButton on={panelKwh} onClick={() => set((s) => ({ panelKwh: !s.panelKwh }))} icon={<Zap size={14} />} label={t("editor.layers.panelKwh")} combo={keyOf("layer.kwh")} mac={mac} />
        <ToggleButton on={sun} onClick={() => set((s) => ({ sun: !s.sun, playing: false }))} icon={<Sun size={14} />} label={t("editor.layers.sun")} combo={keyOf("layer.sun")} mac={mac} />
        {view === "2d" ? (
          <ToggleButton on={props.sectionTool} onClick={props.onSectionTool} icon={<Ruler size={14} />} label={t("editor.layers.section")} combo={keyOf("tool.section")} mac={mac} />
        ) : (
          <ToggleButton on={cloud !== "off"} onClick={() => set((s) => ({ cloud: s.cloud === "off" ? "points" : "off" }))} icon={<Box size={14} />} label={t("editor.layers.cloud")} combo={keyOf("layer.height")} mac={mac} />
        )}
      </div>

      {!nothing && props.loading && <div className="an-card muted small">{t("editor.dock.loading")}</div>}
      {!nothing && props.error && <div className="an-card warn-text small">{props.error}</div>}

      {data && active && (
        <div className="an-card">
          <Legend scale={active.scale} domain={active.domain} label={t(active.label)} units={active.units} format={formatter(intl, active)} />
          {raster === "irradiance" && (
            <label className="an-row">
              <span className="an-month">{month === 0 ? t("editor.dock.year") : intl.formatDate(Date.UTC(2026, month - 1, 15), { month: "short", timeZone: "UTC" })}</span>
              <input type="range" min={0} max={12} step={1} value={month} onChange={(e) => set({ month: Number(e.target.value) })} aria-label={t("editor.dock.month")} disabled={!data.monthly} />
              <span className="muted small" title={t("editor.dock.prevNextMonth")}>
                {formatCombo(keyOf("layer.monthPrev") ?? "[", mac)} {formatCombo(keyOf("layer.monthNext") ?? "]", mac)}
              </span>
            </label>
          )}
          {raster === "height" && (
            <>
              <label className="an-row small">
                <input type="checkbox" checked={contours} onChange={(e) => set({ contours: e.target.checked })} /> {t("editor.dock.contours")}
                <span className="muted">{formatCombo(keyOf("layer.contours") ?? "shift+c", mac)}</span>
              </label>
              <p className="muted small an-note">{t("editor.dock.heightNote")}</p>
            </>
          )}
          {(raster === "access" || raster === "tsrf") && (
            <p className="muted small an-note">
              {raster === "access"
                ? t("editor.dock.accessNote")
                : t("editor.dock.tsrfNote", { tilt: intl.formatNumber(Math.round(data.sun.optimal_tilt_deg)), azimuth: intl.formatNumber(Math.round(data.sun.optimal_azimuth_deg)) })}
            </p>
          )}
          {view === "2d" && <div ref={readout} className="an-readout" aria-live="off">{t("editor.dock.pointAtMap")}</div>}
        </div>
      )}

      {data && kwh && (
        <div className="an-card">
          <Legend scale="sun" domain={kwh.domain} label={t("editor.layers.panelProduction")} units={t("editor.layers.kwhPerYear")} format={(v) => intl.formatNumber(Math.round(v))} />
          <div className="small">
            {t("editor.dock.kwhSummary", { count: yields!.length, total: intl.formatNumber(Math.round(kwh.total)) })}
            {kwh.estimated > 0 && <span className="muted"> · {t("editor.dock.kwhEstimated", { count: kwh.estimated })}</span>}
          </div>
        </div>
      )}

      {data && sun && sunNow && (
        <div className="an-card an-sun">
          <SunPath lat={data.sun.lat} day={day} minutes={minutes} horizon={horizon} />
          <div className="an-sun-controls">
            <div className="an-row">
              <button type="button" className="an-play" onClick={() => set((s) => ({ playing: !s.playing }))} title={t("editor.dock.withKey", { label: playing ? t("editor.dock.pause") : t("editor.dock.play"), combo: formatCombo(keyOf("sun.play") ?? "shift+u", mac) })} aria-label={playing ? t("editor.dock.pause") : t("editor.dock.play")}>
                {playing ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <b>{formatDayOf(intl, day)}</b>
              <span dir="ltr">{formatSolarTimeOf(intl, minutes)}</span>
              <span className="muted small">{t("editor.dock.solarTime")}</span>
            </div>
            <label className="an-row small">
              <span className="an-lbl">{t("editor.dock.date")}</span>
              <input type="range" min={1} max={365} value={day} onChange={(e) => set({ day: Number(e.target.value), playing: false })} aria-label={t("editor.dock.dayOfYear")} />
            </label>
            <label className="an-row small">
              <span className="an-lbl">{t("editor.dock.time")}</span>
              <input type="range" min={span ? Math.floor(span.rise) : 0} max={span ? Math.ceil(span.set) : 1440} step={5} value={minutes} onChange={(e) => set({ minutes: Number(e.target.value), playing: false })} aria-label={t("editor.dock.timeOfDay")} />
            </label>
            <div className="small muted">
              {sunNow.elevation > 0
                ? t("editor.dock.sunPosition", { azimuth: intl.formatNumber(Math.round(sunNow.azimuth)), elevation: intl.formatNumber(Math.round(sunNow.elevation)) })
                : t("editor.dock.sunBelow")}
              {span && ` · ${t("editor.dock.daySpan", { rise: formatSolarTimeOf(intl, span.rise), set: formatSolarTimeOf(intl, span.set) })}`}
            </div>
            <div className="small muted">
              {t("editor.dock.stepHint", {
                earlier: formatCombo(keyOf("sun.earlier") ?? ",", mac),
                later: formatCombo(keyOf("sun.later") ?? ".", mac),
                weekBack: formatCombo(keyOf("sun.dayBack") ?? "<", mac),
                weekOn: formatCombo(keyOf("sun.dayOn") ?? ">", mac),
              })}
            </div>
            {view === "2d" ? <div className="small muted">{t("editor.dock.shade2dNote")}</div> : null}
          </div>
        </div>
      )}

      {data && section && profileData && (
        <div className="an-card">
          <div className="an-row" style={{ justifyContent: "space-between" }}>
            <b className="small">{t("editor.dock.sectionTitle", { length: intl.formatNumber(Math.hypot(section.b[0] - section.a[0], section.b[1] - section.a[1]), { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}</b>
            <button type="button" className="icon-btn" onClick={() => set({ section: null })} aria-label={t("editor.dock.clearSection")}>
              <X size={14} />
            </button>
          </div>
          <ProfileChart distances={profileData.distances} series={profileData.series} />
          <div className="muted small">{t("editor.dock.heightAxis")}</div>
        </div>
      )}

      {data && view === "3d" && cloud !== "off" && (
        <div className="an-card">
          <div className="an-row small">
            <b>{t("editor.layers.cloud")}</b>
            <div className="segmented">
              {(["points", "surface"] as Cloud[]).map((c) => (
                <button key={c} type="button" className={cloud === c ? "on" : ""} onClick={() => set({ cloud: c })}>
                  {c === "points" ? t("editor.dock.cloud.points") : t("editor.dock.cloud.surface")}
                </button>
              ))}
            </div>
          </div>
          <Legend scale="height" domain={{ min: 0, max: Math.max(3, Math.ceil(data.heightAboveGround?.max ?? 10)) }} label={t("editor.dock.raster.heightAboveGround")} units="m" format={(v) => intl.formatNumber(v)} />
          <div className="muted small">{t("editor.dock.cloudNote")}</div>
        </div>
      )}
    </div>
  );
}

/** The analysis rows of the 2D Layers menu (L): the same toggles as the dock, with their keys. */
export function AnalysisMenuRows({ store, keyOf, mac }: { store: AnalysisStore; keyOf: (id: string) => string | null; mac: boolean }) {
  const raster = useAnalysisState(store, (s) => s.raster);
  const panelKwh = useAnalysisState(store, (s) => s.panelKwh);
  const sun = useAnalysisState(store, (s) => s.sun);
  const t = useT();
  const rows: { label: string; on: boolean; action: string; toggle: () => void }[] = [
    { label: t("editor.layers.height"), on: raster === "height", action: "layer.height", toggle: () => store.set((s) => ({ raster: s.raster === "height" ? "none" : "height" })) },
    { label: t("editor.layers.irradiance"), on: raster === "irradiance", action: "layer.irradiance", toggle: () => store.set((s) => ({ raster: s.raster === "irradiance" ? "none" : "irradiance" })) },
    { label: t("editor.layers.access"), on: raster === "access", action: "layer.access", toggle: () => store.set((s) => ({ raster: s.raster === "access" ? "none" : "access" })) },
    { label: t("editor.layers.tsrf"), on: raster === "tsrf", action: "layer.tsrf", toggle: () => store.set((s) => ({ raster: s.raster === "tsrf" ? "none" : "tsrf" })) },
    { label: t("editor.layers.panelProduction"), on: panelKwh, action: "layer.kwh", toggle: () => store.set((s) => ({ panelKwh: !s.panelKwh })) },
    { label: t("editor.layers.sun"), on: sun, action: "layer.sun", toggle: () => store.set((s) => ({ sun: !s.sun, playing: false })) },
  ];
  return (
    <div className="an-menu">
      <b className="small">{t("editor.layers.analysis")}</b>
      {rows.map((r) => (
        <label key={r.action} className="row small">
          <input type="checkbox" checked={r.on} onChange={r.toggle} /> {r.label}
          {keyOf(r.action) && <span className="muted an-menu-key">{formatCombo(keyOf(r.action)!, mac)}</span>}
        </label>
      ))}
    </div>
  );
}
